import React, { useState } from 'react';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import { useApi } from '../../hooks/useApi';
import { updateKid, deleteKid, archiveKid, unarchiveKid } from '../../lib/api/kids';
import { useError } from '../../contexts/ErrorContext';

export default function EditKidModal({ kid, onClose, onSuccess }) {
  const { showError } = useError();
  const { execute: execUpdate, loading: updateLoading } = useApi(updateKid);
  const { execute: execDelete, loading: deleteLoading } = useApi(deleteKid);
  const { execute: execArchive, loading: archiveLoading } = useApi(archiveKid);
  const { execute: execUnarchive, loading: unarchiveLoading } = useApi(unarchiveKid);

  const [formData, setFormData] = useState({
    name: kid.name,
    allowance: kid.allowance_amount?.toString() || '',
    interval: kid.allowance_interval || 'weekly',
    allowance_day: kid.allowance_day || 'monday',
    allowance_time: kid.allowance_time ? kid.allowance_time.substring(0, 5) : '09:00'
  });

  const handleCurrencyChange = (value) => {
    const regex = /^\d*\.?\d{0,2}$/;
    if (value === '' || regex.test(value)) {
      setFormData(prev => ({ ...prev, allowance: value }));
    }
  };

  const handleIntervalChange = (e) => {
    const newInterval = e.target.value;
    let newDay = formData.allowance_day;
    if (newInterval === 'weekly') newDay = 'monday';
    if (newInterval === 'monthly') newDay = '1';
    setFormData({ ...formData, interval: newInterval, allowance_day: newDay });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return showError("Name is required");

    const allowanceNum = formData.allowance ? parseFloat(formData.allowance) : null;

    await execUpdate(kid.id, {
      name: formData.name.trim(),
      allowance_amount: allowanceNum,
      allowance_interval: formData.interval,
      allowance_day: (allowanceNum && formData.interval !== 'daily') ? formData.allowance_day : null,
      allowance_time: allowanceNum ? formData.allowance_time : null
    });
    
    onSuccess();
    onClose();
  };

  const handleDelete = async () => {
    if (!window.confirm(`Permanently delete ${kid.name}? This cannot be undone.`)) return;
    await execDelete(kid.id);
    onSuccess();
    onClose();
  };

  const handleArchiveToggle = async () => {
    if (kid.is_archived) {
      await execUnarchive(kid.id);
    } else {
      if (!window.confirm(`Archive ${kid.name}?`)) return;
      await execArchive(kid.id);
    }
    onSuccess();
    onClose();
  };

  const loading = updateLoading || deleteLoading || archiveLoading || unarchiveLoading;
  const hasAllowance = formData.allowance && parseFloat(formData.allowance) > 0;

  return (
    <Modal title={`Edit ${kid.name}`} onClose={onClose}>
      <form onSubmit={handleSave} className="space-y-5">
        <Input 
          label="Child's Name *" 
          value={formData.name} 
          onChange={e => setFormData({ ...formData, name: e.target.value })} 
        />

        <div className="pt-4 border-t border-gray-100">
          <h4 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Allowance Settings</h4>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <Input 
              label="Amount ($)" 
              type="text" 
              inputMode="decimal" 
              placeholder="0.00" 
              value={formData.allowance} 
              onChange={e => handleCurrencyChange(e.target.value)} 
            />
            <div className="flex flex-col space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">Interval</label>
              <select 
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-sm h-[42px]"
                value={formData.interval}
                onChange={handleIntervalChange}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>

          {/* Scheduling Section */}
          {hasAllowance && (
            <div className={`grid gap-3 ${formData.interval === 'daily' ? 'grid-cols-1' : 'grid-cols-2'}`}>
              {formData.interval === 'weekly' && (
                <div className="flex flex-col space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700">Day of Week</label>
                  <select 
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-sm h-[42px]"
                    value={formData.allowance_day}
                    onChange={e => setFormData({ ...formData, allowance_day: e.target.value })}
                  >
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                </div>
              )}

              {formData.interval === 'monthly' && (
                <div className="flex flex-col space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700">Day of Month</label>
                  <select 
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-sm h-[42px]"
                    value={formData.allowance_day}
                    onChange={e => setFormData({ ...formData, allowance_day: e.target.value })}
                  >
                    {Array.from({ length: 28 }, (_, i) => (
                      <option key={i + 1} value={(i + 1).toString()}>{i + 1}</option>
                    ))}
                  </select>
                </div>
              )}

              <Input 
                label="Time" 
                type="time" 
                value={formData.allowance_time} 
                onChange={e => setFormData({ ...formData, allowance_time: e.target.value })} 
              />
            </div>
          )}
        </div>

        <div className="pt-6 flex flex-col space-y-4">
          <div className="flex justify-between items-center gap-3">
            <Button 
              variant="secondary" 
              type="button" 
              onClick={handleArchiveToggle} 
              icon={kid.is_archived ? "ArrowUp" : "Archive"}
              className="flex-1"
            >
              {kid.is_archived ? 'Unarchive' : 'Archive'}
            </Button>
            <Button 
              variant="danger" 
              type="button" 
              onClick={handleDelete} 
              icon="Trash2"
              className="flex-1"
            >
              Delete
            </Button>
          </div>
          
          <div className="flex justify-end space-x-3 border-t border-gray-100 pt-5">
            <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={loading} icon="Save">Save Changes</Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}